import { formatDate } from "@angular/common";
import { inject, Injectable, signal } from "@angular/core";
import { CellDataType } from "@models/grid/cell-data-type.enum";
import { PageResult } from "@models/requests/page-result.interface";
import { TextMessageRecord } from "@models/text-messages-templates/text-message-record";
import { TextMessageStatus } from "@models/text-messages-templates/text-message-status.enum";
import {
  AgOutgoingTextMessageStatusComponent
} from "@pages/outgoing-messages/components/outgoing-sms-messages-page/components/ag-outgoing-text-message-status/ag-outgoing-text-message-status.component";
import { BaseGridService } from "@shared/services/base/base-grid.service";
import { TextMessageApiService } from "@shared/services/sms-templates.service";
import { convertEnumToFilterOption } from "@shared/utils/enum-to-filter-option.helper";
import { dateTimeComparator } from "@shared/utils/grid/date-time-comparator";
import { ColDef, IServerSideDatasource, IServerSideGetRowsParams, ValueFormatterParams } from "ag-grid-community";
import { ToastrService } from "ngx-toastr";
import { Subject } from "rxjs";

@Injectable()
export class OutgoingSmsMessagesGridService extends BaseGridService {
  private textMessageApiService = inject(TextMessageApiService);
  private toastr = inject(ToastrService);

  totalRecords = signal<number>(0);
  dataSource = signal<IServerSideDatasource>(this.getDataSource());

  dataLoaded$ = new Subject<PageResult<TextMessageRecord>>();

  // filters
  readonly textMessagesStatusFilterOptions = convertEnumToFilterOption(TextMessageStatus);

  setDataSource(searchText?: string, endRow?: number): void {
    this.dataSource.set(this.getDataSource(searchText, endRow));
  }

  getDataSource(searchText = '', endRow?: number): IServerSideDatasource {
    return {
      getRows: (params: IServerSideGetRowsParams) => {
        const request = params.request;

        if (endRow) {
          params.request.endRow = endRow;
        }

        this.textMessageApiService.getOutgoingTextMessagesList(request, searchText)
          .subscribe({
            next: (result) => {
              this.totalRecords.set(result.total);

              params.success({
                rowData: result.records,
                rowCount: result.total,
              });

              this.dataLoaded$.next(result);
            },
            error: (err) => {
              this.toastr.error('Something happened while loading outgoing text messages', 'Error');
              console.error(err);
              params.fail();
            }
          });
      }
    };
  }

  setColDefsWithCellClickHandler(actionColumn: ColDef, cellClickHandler: Function) {
    const colDefs = [
      {
        headerName: 'Date',
        field: 'createdOn',
        sort: 'desc',
        width: 200,
        minWidth: 200,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: dateTimeComparator,
        },
        cellDataType: CellDataType.DATE,
        valueFormatter: (params: ValueFormatterParams<TextMessageRecord>) => {
          return formatDate(params.data?.createdOn ?? '', 'dd MMM yyyy HH:mm', 'en-US');
        },
        menuTabs: ['generalMenuTab'],
        onCellClicked: cellClickHandler,
      },
      {
        headerName: 'Header',
        field: 'code',
        valueFormatter: (params: ValueFormatterParams<TextMessageRecord>) => params.data?.header ?? '',
        onCellClicked: cellClickHandler,
        cellClass: 'hover-underline'
      },
      {
        headerName: 'Recipients',
        field: 'to',
        flex: 2,
        menuTabs: ['generalMenuTab'],
        onCellClicked: cellClickHandler,
        cellClass: 'hover-underline'
      },
      {
        headerName: 'Status',
        field: 'status',
        cellRenderer: AgOutgoingTextMessageStatusComponent,
        width: 105,
        minWidth: 105,
        filter: 'agSetColumnFilter',
        filterParams: {
          filterOptions: this.textMessagesStatusFilterOptions,
          values: Object.values(TextMessageStatus).filter((v) => !isNaN(Number(v))),
        },
        menuTabs: ['generalMenuTab']
      },
      // {
      //   headerName: 'Sender',
      //   field: 'from',
      //   flex: 1,
      //   hide: true,
      //   cellClass: 'hover-underline',
      //   onCellClicked: this.cellClickHandler.bind(this),
      // },
      actionColumn
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));

    this.colDefs.set(colDefs);
  }
}
